import { useState } from "react";
import { CheckCircle2, ChevronDown, ChevronUp } from "lucide-react";
import { OnScreenModal } from "@/components/ui/OnScreenModal";
import type { VerifastChecklistSection } from "../../utils/verifastChecksMapper";

interface VerifastChecksDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  sections: VerifastChecklistSection[];
}

export function VerifastChecksDetailModal({
  isOpen,
  onClose,
  sections,
}: VerifastChecksDetailModalProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (title: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="ID Verification Details"
      onClose={onClose}
      maxWidth="max-w-3xl"
      bodyScrollOnly
      headerVariant="centeredTitle"
      overlayClassName="!z-[10002]"
      overlayDataTestId="verifast-checks-detail-modal"
      panelDataTestId="verifast-checks-detail-panel"
      titleDataTestId="verifast-checks-detail-title"
      closeButtonDataTestId="verifast-checks-detail-close-button"
    >
      <div className="flex flex-col gap-4">
        {sections.map((section) => {
          const expanded = expandedSections[section.title] ?? true;
          return (
            <section
              key={section.title}
              className="overflow-hidden rounded-lg border border-slate-200 bg-white"
            >
              <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-4 py-3">
                <h3 className="text-base font-semibold text-slate-900">
                  {section.title}
                </h3>
                <span className="inline-flex items-center gap-1.5 text-sm font-medium text-emerald-700">
                  <CheckCircle2 className="h-4 w-4" aria-hidden />
                  {section.allRequiredPassed
                    ? "All Required Checks Passed"
                    : "Checks recorded"}
                </span>
              </div>

              <button
                type="button"
                className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm font-medium text-[#1597ff] hover:bg-slate-50"
                onClick={() => toggleSection(section.title)}
                aria-expanded={expanded}
              >
                {expanded ? (
                  <ChevronUp className="h-4 w-4" aria-hidden />
                ) : (
                  <ChevronDown className="h-4 w-4" aria-hidden />
                )}
                {expanded ? "Collapse checks" : "Expand to view all checks"}
              </button>

              {expanded ? (
                <div className="grid gap-2 px-4 pb-4 sm:grid-cols-2">
                  {section.items.map((item) => (
                    <div
                      key={item.key}
                      className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
                    >
                      <span className="text-sm text-slate-800">
                        {item.label}
                        {item.optional ? "*" : ""}
                      </span>
                      <CheckCircle2
                        className={`h-4 w-4 shrink-0 ${
                          item.passed ? "text-emerald-600" : "text-rose-500"
                        }`}
                        aria-label={item.passed ? "Passed" : "Failed"}
                      />
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          );
        })}

        <p className="m-0 text-xs text-slate-500">
          *Checks marked with an asterisk are not required.
        </p>
      </div>
    </OnScreenModal>
  );
}
